import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Textarea } from '../components/ui/textarea';
import MapEmbed from '../components/MapEmbed';
import SEO from '../components/SEO';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Phone,
  MessageCircle,
  MapPin,
  Star,
  Clock,
  Globe,
  Mail,
  Navigation,
  X,
  ChevronLeft,
  ChevronRight,
  Camera
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BusinessDetailPage = () => {
  const { businessId } = useParams();
  const navigate = useNavigate();
  const { language, t } = useLanguage();
  const { isAuthenticated, user } = useAuth();
  
  const [business, setBusiness] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newReview, setNewReview] = useState({ rating: 5, comment: '' });
  const [submitting, setSubmitting] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  useEffect(() => {
    fetchData();
  }, [businessId]);

  // Keyboard navigation for lightbox
  useEffect(() => {
    if (!lightboxOpen) return;
    
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        closeLightbox();
      } else if (e.key === 'ArrowLeft') {
        prevImage();
      } else if (e.key === 'ArrowRight') {
        nextImage();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [lightboxOpen, currentImageIndex]);

  const fetchData = async () => {
    try {
      const [bizRes, reviewRes] = await Promise.all([
        axios.get(`${API}/businesses/${businessId}`),
        axios.get(`${API}/businesses/${businessId}/reviews`)
      ]);
      setBusiness(bizRes.data);
      setReviews(reviewRes.data);
    } catch (error) {
      console.error('Error fetching business:', error);
      toast.error(t('error'));
    } finally {
      setLoading(false);
    }
  };

  const handleCall = () => {
    window.location.href = `tel:${business.phone}`;
  };

  const handleWhatsApp = () => {
    const phone = business.whatsapp || business.phone;
    window.open(`https://wa.me/${phone.replace(/\D/g, '')}`, '_blank');
  };

  const handleDirections = () => {
    window.open(
      `https://www.google.com/maps/dir/?api=1&destination=${business.latitude},${business.longitude}`,
      '_blank'
    );
  };

  const handleSubmitReview = async (e) => {
    e.preventDefault();
    if (!isAuthenticated()) {
      navigate('/login');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/reviews`, {
        business_id: businessId,
        rating: newReview.rating,
        comment: newReview.comment
      });
      toast.success(language === 'ta' ? 'விமர்சனம் சேர்க்கப்பட்டது' : 'Review added successfully');
      setNewReview({ rating: 5, comment: '' });
      fetchData();
    } catch (error) {
      toast.error(t('error'));
    } finally {
      setSubmitting(false);
    }
  };

  const openLightbox = (index) => {
    setCurrentImageIndex(index);
    setLightboxOpen(true);
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
  };

  const nextImage = () => {
    if (business?.images) {
      setCurrentImageIndex((prevIndex) => 
        prevIndex === business.images.length - 1 ? 0 : prevIndex + 1
      );
    }
  };

  const prevImage = () => {
    if (business?.images) {
      setCurrentImageIndex((prevIndex) => 
        prevIndex === 0 ? business.images.length - 1 : prevIndex - 1
      );
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-pulse text-slate-500">{t('loading')}</div>
      </div>
    );
  }

  if (!business) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-slate-500 text-lg mb-4">{t('error')}</p>
          <Button onClick={() => navigate(-1)}>{t('home')}</Button>
        </div>
      </div>
    );
  }

  const name = language === 'ta' && business.name_ta ? business.name_ta : business.name;
  const description = language === 'ta' && business.description_ta ? business.description_ta : business.description;
  const address = language === 'ta' && business.address_ta ? business.address_ta : business.address;

  // Get business images or default images
  const businessImages = business.images && business.images.length > 0 
    ? business.images 
    : ['https://images.unsplash.com/photo-1556740738-b6a63e27c4df?w=1200'];

  // Structured data for business detail page
  const businessStructuredData = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "name": name,
    "description": description,
    "address": {
      "@type": "PostalAddress",
      "streetAddress": address,
      "addressLocality": "Karur",
      "addressRegion": "Tamil Nadu",
      "addressCountry": "IN"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": business.latitude,
      "longitude": business.longitude
    },
    "telephone": business.phone,
    "image": businessImages[0],
    ...(business.avg_rating > 0 && {
      "aggregateRating": {
        "@type": "AggregateRating",
        "ratingValue": business.avg_rating,
        "reviewCount": business.review_count
      }
    }),
    ...(business.website && { "url": business.website }),
    ...(business.email && { "email": business.email })
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-50" data-testid="business-detail-page">
      <SEO
        title={`${name} | ${language === 'ta' ? business.category_name_ta : business.category_name_en} in Karur`}
        description={`${description} Contact: ${business.phone}. Read reviews, get directions, and find all details about ${name} in Karur.`}
        keywords={`${name}, ${business.category_name_en} Karur, ${address}, ${business.phone}, local business`}
        ogImage={businessImages[0]}
        ogType="business.business"
        structuredData={businessStructuredData}
      />
      
      {/* Enhanced Lightbox Modal */}
      {lightboxOpen && (
        <div className="fixed inset-0 z-50 bg-black/97 backdrop-blur-md flex items-center justify-center animate-in fade-in duration-300">
          {/* Close Button */}
          <button
            onClick={closeLightbox}
            className="absolute top-6 right-6 text-white hover:text-gray-300 bg-white/10 hover:bg-white/20 backdrop-blur-sm p-3 rounded-full transition-all hover:scale-110 shadow-xl z-50"
            aria-label="Close"
          >
            <X className="h-6 w-6" />
          </button>
          
          {/* Navigation Arrows */}
          {businessImages.length > 1 && (
            <>
              <button
                onClick={prevImage}
                className="absolute left-6 text-white hover:text-gray-300 bg-white/10 hover:bg-white/20 backdrop-blur-sm p-4 rounded-full transition-all hover:scale-110 shadow-xl z-50"
                aria-label="Previous"
              >
                <ChevronLeft className="h-8 w-8" />
              </button>
              <button
                onClick={nextImage}
                className="absolute right-6 text-white hover:text-gray-300 bg-white/10 hover:bg-white/20 backdrop-blur-sm p-4 rounded-full transition-all hover:scale-110 shadow-xl z-50"
                aria-label="Next"
              >
                <ChevronRight className="h-8 w-8" />
              </button>
            </>
          )}
          
          {/* Main Image */}
          <div className="relative max-h-[85vh] max-w-[90vw] animate-in zoom-in duration-300">
            <img
              src={businessImages[currentImageIndex]}
              alt={`${name} - ${currentImageIndex + 1}`}
              className="max-h-[85vh] max-w-[90vw] object-contain rounded-lg shadow-2xl"
            />
          </div>
          
          {/* Image Counter & Info */}
          <div className="absolute bottom-8 left-1/2 -translate-x-1/2 bg-white/10 backdrop-blur-md text-white px-6 py-3 rounded-full shadow-xl border border-white/20">
            <div className="flex items-center gap-4">
              <Camera className="h-5 w-5" />
              <span className="font-semibold text-lg">
                {currentImageIndex + 1} / {businessImages.length}
              </span>
              <span className="text-sm text-white/80 hidden sm:inline">
                {language === 'ta' ? 'அம்பு விசைகளைப் பயன்படுத்தவும்' : 'Use arrow keys to navigate'}
              </span>
            </div>
          </div>
          
          {/* Thumbnail Strip */}
          {businessImages.length > 1 && (
            <div className="absolute bottom-24 left-1/2 -translate-x-1/2 max-w-4xl w-full px-4 overflow-hidden">
              <div className="flex gap-3 overflow-x-auto scrollbar-hide justify-center pb-2">
                {businessImages.map((image, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentImageIndex(index)}
                    className={`flex-shrink-0 w-16 h-16 md:w-20 md:h-20 rounded-lg overflow-hidden border-2 transition-all duration-300 ${
                      currentImageIndex === index
                        ? 'border-white shadow-2xl scale-110 ring-2 ring-white/50'
                        : 'border-white/30 hover:border-white/60 opacity-60 hover:opacity-100'
                    }`}
                  >
                    <img
                      src={image}
                      alt={`Thumbnail ${index + 1}`}
                      className="w-full h-full object-cover"
                    />
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Enhanced Header Image Carousel */}
      <div className="relative h-80 md:h-[32rem] lg:h-[36rem] bg-slate-900">
        {/* Main Image Display */}
        <div className="relative h-full overflow-hidden group">
          <img
            src={businessImages[currentImageIndex]}
            alt={`${name} - ${currentImageIndex + 1}`}
            className="w-full h-full object-cover cursor-pointer transition-all duration-500 group-hover:scale-105"
            onClick={() => openLightbox(currentImageIndex)}
          />
          
          {/* Gradient Overlays for better text visibility */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-r from-black/40 via-transparent to-black/40" />
          
          {/* Navigation Arrows - Only show if multiple images */}
          {businessImages.length > 1 && (
            <>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  prevImage();
                }}
                className="absolute left-4 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 backdrop-blur-sm text-white p-3 rounded-full transition-all duration-300 hover:scale-110 shadow-xl z-20"
                aria-label="Previous image"
              >
                <ChevronLeft className="h-6 w-6" />
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  nextImage();
                }}
                className="absolute right-4 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 backdrop-blur-sm text-white p-3 rounded-full transition-all duration-300 hover:scale-110 shadow-xl z-20"
                aria-label="Next image"
              >
                <ChevronRight className="h-6 w-6" />
              </button>
            </>
          )}
        </div>
        
        {/* Back Button */}
        <Button
          variant="ghost"
          onClick={() => navigate(-1)}
          className="absolute top-6 left-6 text-white hover:bg-white/20 backdrop-blur-md border border-white/20 shadow-lg z-30 transition-all hover:scale-105"
          data-testid="back-btn"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          {t('home')}
        </Button>
        
        {/* Image Counter & Gallery Button */}
        <div className="absolute top-6 right-6 flex gap-3 z-30">
          {businessImages.length > 1 && (
            <>
              <div className="bg-black/60 backdrop-blur-md text-white px-4 py-2 rounded-full text-sm font-medium border border-white/20 shadow-lg">
                {currentImageIndex + 1} / {businessImages.length}
              </div>
              <button
                onClick={() => openLightbox(currentImageIndex)}
                className="bg-black/60 hover:bg-black/80 backdrop-blur-md text-white px-4 py-2 rounded-full text-sm font-medium flex items-center gap-2 border border-white/20 shadow-lg transition-all hover:scale-105"
              >
                <Camera className="h-4 w-4" />
                {language === 'ta' ? 'அனைத்தையும் பார்க்க' : 'View All'}
              </button>
            </>
          )}
        </div>
        
        {/* Thumbnail Strip at Bottom - Only show if multiple images */}
        {businessImages.length > 1 && (
          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-20 max-w-4xl w-full px-4">
            <div className="flex gap-3 overflow-x-auto scrollbar-hide justify-center pb-2">
              {businessImages.slice(0, 5).map((image, index) => (
                <button
                  key={index}
                  onClick={(e) => {
                    e.stopPropagation();
                    setCurrentImageIndex(index);
                  }}
                  className={`flex-shrink-0 w-20 h-20 md:w-24 md:h-24 rounded-xl overflow-hidden border-3 transition-all duration-300 hover:scale-110 ${
                    currentImageIndex === index
                      ? 'border-white shadow-2xl ring-2 ring-white'
                      : 'border-white/40 hover:border-white/80 opacity-70 hover:opacity-100'
                  }`}
                >
                  <img
                    src={image}
                    alt={`Thumbnail ${index + 1}`}
                    className="w-full h-full object-cover"
                  />
                </button>
              ))}
              {businessImages.length > 5 && (
                <button
                  onClick={() => openLightbox(5)}
                  className="flex-shrink-0 w-20 h-20 md:w-24 md:h-24 rounded-xl overflow-hidden border-3 border-white/40 bg-black/60 backdrop-blur-sm hover:bg-black/80 transition-all hover:scale-110 flex items-center justify-center"
                >
                  <div className="text-white text-center">
                    <Camera className="h-6 w-6 mx-auto mb-1" />
                    <span className="text-xs font-medium">+{businessImages.length - 5}</span>
                  </div>
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      <div className="container mx-auto px-4 md:px-6 lg:px-8 -mt-20 relative z-10 pb-16">
        {/* Main Info Card with Enhanced Design */}
        <Card className="mb-8 shadow-2xl border-2 border-white/80 backdrop-blur-sm bg-white/95 overflow-hidden hover-lift">
          <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500"></div>
          <CardContent className="p-8 md:p-10">
            <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-6">
              <div className="flex-1">
                <div className="flex flex-wrap items-center gap-3 mb-4">
                  <span className="bg-gradient-to-r from-blue-600 via-blue-500 to-indigo-600 text-white text-sm font-bold px-5 py-2 rounded-full shadow-lg hover:shadow-xl transition-all hover:scale-105 cursor-default">
                    {language === 'ta' ? business.category_name_ta : business.category_name_en}
                  </span>
                  {business.avg_rating > 0 && (
                    <div className="flex items-center gap-1.5 bg-gradient-to-r from-yellow-100 to-amber-100 px-4 py-2 rounded-full border-2 border-yellow-200 shadow-md hover:shadow-lg transition-all hover:scale-105">
                      <Star className="h-5 w-5 fill-yellow-500 text-yellow-500" />
                      <span className="font-bold text-yellow-700 text-lg">{business.avg_rating}</span>
                      <span className="text-slate-600 text-sm font-medium">({business.review_count})</span>
                    </div>
                  )}
                </div>
                <h1 className="text-4xl md:text-5xl font-extrabold text-slate-900 font-['Manrope'] mb-4 leading-tight">
                  <span className="bg-gradient-to-r from-slate-900 via-blue-900 to-slate-800 bg-clip-text text-transparent">
                    {name}
                  </span>
                </h1>
                <p className="text-slate-600 text-lg md:text-xl leading-relaxed max-w-3xl">{description}</p>
              </div>
            </div>

            {/* Action Buttons with Enhanced Styling */}
            <div className="flex flex-wrap gap-4 mt-10 pt-8 border-t-2 border-slate-100">
              <Button
                onClick={handleCall}
                className="flex-1 min-w-[140px] bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 shadow-lg hover:shadow-2xl transition-all duration-300 hover:scale-105 py-6 text-base font-bold"
                data-testid="call-btn"
              >
                <Phone className="mr-2 h-5 w-5" />
                {t('call')}
              </Button>
              {business.whatsapp && (
                <Button
                  onClick={handleWhatsApp}
                  className="flex-1 min-w-[140px] bg-gradient-to-r from-[#25D366] to-[#20bd5a] hover:from-[#20bd5a] hover:to-[#1ca850] shadow-lg hover:shadow-2xl transition-all duration-300 hover:scale-105 py-6 text-base font-bold"
                  data-testid="whatsapp-btn"
                >
                  <MessageCircle className="mr-2 h-5 w-5" />
                  {t('whatsapp')}
                </Button>
              )}
              <Button
                onClick={handleDirections}
                variant="outline"
                className="flex-1 min-w-[140px] border-2 border-blue-600 text-blue-700 hover:bg-blue-600 hover:text-white shadow-md hover:shadow-xl transition-all duration-300 hover:scale-105 py-6 text-base font-bold"
                data-testid="directions-btn"
              >
                <Navigation className="mr-2 h-5 w-5" />
                {t('getDirections')}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Enhanced Photo Gallery */}
        {businessImages.length > 1 && (
          <Card className="mb-6 shadow-2xl border-2 border-white/50 overflow-hidden bg-gradient-to-br from-white to-blue-50/30">
            <CardHeader className="bg-gradient-to-r from-blue-500 via-indigo-500 to-purple-500 text-white border-b-0">
              <CardTitle className="flex items-center justify-between text-xl">
                <div className="flex items-center gap-3">
                  <div className="bg-white/20 backdrop-blur-sm p-2 rounded-lg">
                    <Camera className="h-6 w-6" />
                  </div>
                  <div>
                    <div className="font-bold text-xl">
                      {language === 'ta' ? 'கடை புகைப்படங்கள்' : 'Photo Gallery'}
                    </div>
                    <div className="text-sm font-normal text-white/80 mt-0.5">
                      {businessImages.length} {language === 'ta' ? 'படங்கள் கிடைக்கின்றன' : 'photos available'}
                    </div>
                  </div>
                </div>
                <div className="bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full text-sm font-bold">
                  {businessImages.length}+ {language === 'ta' ? 'படங்கள்' : 'Photos'}
                </div>
              </CardTitle>
            </CardHeader>
            <CardContent className="p-8">
              {/* Featured Large Images Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                {businessImages.slice(0, 2).map((image, index) => (
                  <div
                    key={index}
                    className="relative aspect-[4/3] group cursor-pointer overflow-hidden rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:scale-[1.02]"
                    onClick={() => openLightbox(index)}
                  >
                    <img
                      src={image}
                      alt={`${name} - Featured Photo ${index + 1}`}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-all duration-300">
                      <div className="absolute bottom-4 left-4 right-4 text-white">
                        <div className="flex items-center justify-between">
                          <span className="font-bold text-lg drop-shadow-lg">
                            {language === 'ta' ? 'பார்க்க கிளிக் செய்யவும்' : 'Click to view full size'}
                          </span>
                          <div className="bg-white/30 backdrop-blur-sm px-3 py-1 rounded-full text-sm">
                            {index + 1}/{businessImages.length}
                          </div>
                        </div>
                      </div>
                    </div>
                    {/* Zoom Icon */}
                    <div className="absolute top-4 right-4 bg-white/90 backdrop-blur-sm p-2 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300 shadow-lg">
                      <Camera className="h-5 w-5 text-slate-700" />
                    </div>
                  </div>
                ))}
              </div>
              
              {/* Smaller Images Grid */}
              {businessImages.length > 2 && (
                <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                  {businessImages.slice(2).map((image, index) => (
                    <div
                      key={index + 2}
                      className="relative aspect-square group cursor-pointer overflow-hidden rounded-xl shadow-md hover:shadow-2xl transition-all duration-500 transform hover:scale-105 hover:z-10"
                      onClick={() => openLightbox(index + 2)}
                    >
                      <img
                        src={image}
                        alt={`${name} - Photo ${index + 3}`}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-125 group-hover:rotate-2"
                      />
                      {/* Hover Overlay */}
                      <div className="absolute inset-0 bg-gradient-to-t from-blue-600/80 via-purple-600/40 to-transparent opacity-0 group-hover:opacity-100 transition-all duration-300 flex items-center justify-center">
                        <div className="text-white text-center transform translate-y-4 group-hover:translate-y-0 transition-transform duration-300">
                          <Camera className="h-6 w-6 mx-auto mb-1 drop-shadow-lg" />
                          <span className="text-xs font-semibold drop-shadow-lg">
                            {language === 'ta' ? 'பார்க்க' : 'View'}
                          </span>
                        </div>
                      </div>
                      {/* Image Number Badge */}
                      <div className="absolute top-2 right-2 bg-black/60 backdrop-blur-sm text-white text-xs px-2 py-1 rounded-full font-medium">
                        {index + 3}
                      </div>
                    </div>
                  ))}
                </div>
              )}
              
              {/* View All Button */}
              <div className="mt-8 text-center">
                <Button
                  onClick={() => openLightbox(0)}
                  className="bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 hover:from-blue-700 hover:via-indigo-700 hover:to-purple-700 text-white px-8 py-6 text-lg shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105"
                >
                  <Camera className="mr-3 h-6 w-6" />
                  {language === 'ta' ? 'அனைத்து புகைப்படங்களையும் பார்க்கவும்' : 'View All Photos in Gallery'}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Details */}
          <div className="lg:col-span-2 space-y-6">
            {/* Contact Info */}
            <Card className="shadow-lg border-2 border-white/50">
              <CardHeader className="bg-gradient-to-r from-slate-50 to-blue-50 border-b">
                <CardTitle className="text-xl">
                  {language === 'ta' ? 'தொடர்பு தகவல்' : 'Contact Information'}
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6 space-y-5">
                <div className="flex items-start gap-4 p-3 rounded-lg hover:bg-slate-50 transition-colors">
                  <div className="bg-blue-100 p-2 rounded-lg">
                    <MapPin className="h-5 w-5 text-blue-600" />
                  </div>
                  <span className="text-slate-700 flex-1">{address}</span>
                </div>
                <div className="flex items-center gap-4 p-3 rounded-lg hover:bg-slate-50 transition-colors">
                  <div className="bg-green-100 p-2 rounded-lg">
                    <Phone className="h-5 w-5 text-green-600" />
                  </div>
                  <a href={`tel:${business.phone}`} className="text-blue-600 hover:text-blue-700 font-medium hover:underline flex-1">
                    {business.phone}
                  </a>
                </div>
                {business.email && (
                  <div className="flex items-center gap-4 p-3 rounded-lg hover:bg-slate-50 transition-colors">
                    <div className="bg-purple-100 p-2 rounded-lg">
                      <Mail className="h-5 w-5 text-purple-600" />
                    </div>
                    <a href={`mailto:${business.email}`} className="text-blue-600 hover:text-blue-700 font-medium hover:underline flex-1">
                      {business.email}
                    </a>
                  </div>
                )}
                {business.website && (
                  <div className="flex items-center gap-4 p-3 rounded-lg hover:bg-slate-50 transition-colors">
                    <div className="bg-indigo-100 p-2 rounded-lg">
                      <Globe className="h-5 w-5 text-indigo-600" />
                    </div>
                    <a href={business.website} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-700 font-medium hover:underline flex-1 truncate">
                      {business.website}
                    </a>
                  </div>
                )}
                {business.working_hours && Object.keys(business.working_hours).length > 0 && (
                  <div className="flex items-start gap-4 p-3 rounded-lg hover:bg-slate-50 transition-colors">
                    <div className="bg-amber-100 p-2 rounded-lg">
                      <Clock className="h-5 w-5 text-amber-600" />
                    </div>
                    <div className="flex-1">
                      {Object.entries(business.working_hours).map(([day, hours]) => (
                        <div key={day} className="flex justify-between text-slate-700 py-1">
                          <span className="font-medium capitalize">{day}</span>
                          <span className="text-slate-600">{hours}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Reviews */}
            <Card className="shadow-lg border-2 border-white/50">
              <CardHeader className="bg-gradient-to-r from-yellow-50 to-amber-50 border-b">
                <CardTitle className="flex items-center gap-2 text-xl">
                  <Star className="h-5 w-5 text-yellow-500" />
                  {t('reviews')} ({reviews.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6 p-6">
                {/* Write Review Form */}
                <form onSubmit={handleSubmitReview} className="space-y-4 p-5 rounded-xl bg-gradient-to-br from-blue-50 to-indigo-50 border-2 border-blue-100">
                  <h4 className="font-semibold text-lg text-slate-800">{t('writeReview')}</h4>
                  <div className="flex items-center gap-2">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <button
                        key={star}
                        type="button"
                        onClick={() => setNewReview({ ...newReview, rating: star })}
                        className="focus:outline-none transition-transform hover:scale-110"
                        data-testid={`star-${star}`}
                      >
                        <Star
                          className={`h-9 w-9 ${
                            star <= newReview.rating
                              ? 'fill-yellow-400 text-yellow-400'
                              : 'text-slate-300'
                          } transition-colors`}
                        />
                      </button>
                    ))}
                  </div>
                  <Textarea
                    placeholder={language === 'ta' ? 'உங்கள் விமர்சனத்தை எழுதுங்கள்...' : 'Write your review...'}
                    value={newReview.comment}
                    onChange={(e) => setNewReview({ ...newReview, comment: e.target.value })}
                    rows={4}
                    className="border-2 focus:border-blue-400"
                    data-testid="review-textarea"
                  />
                  <Button
                    type="submit"
                    disabled={submitting || !newReview.comment.trim()}
                    className="w-full bg-gradient-to-r from-[#E85A24] to-[#d14918] hover:from-[#d14918] hover:to-[#b03d14] shadow-lg hover:shadow-xl transition-all"
                    data-testid="submit-review-btn"
                  >
                    {submitting ? t('loading') : t('submit')}
                  </Button>
                </form>

                {/* Reviews List */}
                {reviews.length === 0 ? (
                  <div className="text-center py-12">
                    <Star className="h-12 w-12 text-slate-300 mx-auto mb-3" />
                    <p className="text-slate-500 text-lg">{t('noReviews')}</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {reviews.map((review) => (
                      <div 
                        key={review.id} 
                        className="p-4 rounded-lg bg-white border border-slate-200 hover:border-blue-300 hover:shadow-md transition-all" 
                        data-testid={`review-${review.id}`}
                      >
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center text-white font-semibold">
                              {review.user_name.charAt(0).toUpperCase()}
                            </div>
                            <span className="font-semibold text-slate-900">{review.user_name}</span>
                          </div>
                          <div className="flex items-center gap-1 bg-yellow-50 px-2 py-1 rounded-full">
                            {[...Array(5)].map((_, i) => (
                              <Star
                                key={i}
                                className={`h-4 w-4 ${
                                  i < review.rating
                                    ? 'fill-yellow-400 text-yellow-400'
                                    : 'text-slate-300'
                                }`}
                              />
                            ))}
                          </div>
                        </div>
                        <p className="text-slate-700 leading-relaxed mb-2">{review.comment}</p>
                        <p className="text-slate-400 text-sm">
                          {new Date(review.created_at).toLocaleDateString(language === 'ta' ? 'ta-IN' : 'en-US', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric'
                          })}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Right Column - Map */}
          <div className="lg:col-span-1">
            <Card className="sticky top-24 shadow-lg border-2 border-white/50">
              <CardHeader className="bg-gradient-to-r from-green-50 to-emerald-50 border-b">
                <CardTitle className="flex items-center gap-2 text-xl">
                  <MapPin className="h-5 w-5 text-green-600" />
                  {t('getDirections')}
                </CardTitle>
              </CardHeader>
              <CardContent className="p-4">
                <div className="rounded-lg overflow-hidden shadow-md border-2 border-slate-200">
                  <MapEmbed
                    latitude={business.latitude}
                    longitude={business.longitude}
                    businessName={name}
                  />
                </div>
                <Button
                  onClick={handleDirections}
                  className="w-full mt-4 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 shadow-lg hover:shadow-xl transition-all"
                  data-testid="map-directions-btn"
                >
                  <Navigation className="mr-2 h-4 w-4" />
                  {language === 'ta' ? 'வழிகளைப் பெறுங்கள்' : 'Open in Google Maps'}
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BusinessDetailPage;
